import { Link } from 'react-router-dom';

const features = [
  {
    icon: 'smart_toy',
    title: 'AI-Native Deploy',
    description: 'Built specifically for AI coding assistants. Cursor, Claude, GitHub Copilot, and other AI tools can deploy your code directly via our API.',
    gradient: 'from-orange-500 to-red-500',
    details: [
      'API key authentication',
      'Compatible with all AI coding assistants',
      'No manual setup required',
      'Deploy with a single API call',
    ],
  },
  {
    icon: 'inventory_2',
    title: 'Container Platform',
    description: 'Run any application in isolated Docker containers. Node.js, Python, Go, Rust—if it runs in Docker, it runs on WiserWeb.',
    gradient: 'from-cyan-500 to-blue-500',
    details: [
      'Docker-based deployments',
      'Any language or framework',
      'Environment variable management',
      'Resource isolation and limits',
    ],
  },
  {
    icon: 'vpn_key',
    title: 'API Key Authentication',
    description: 'Secure API key authentication for programmatic deployments. Generate scoped keys from your dashboard or via API.',
    gradient: 'from-green-500 to-emerald-500',
    details: [
      'Scoped API keys',
      'Dashboard and API management',
      'Key rotation support',
      'Usage tracking and analytics',
    ],
  },
  {
    icon: 'storage',
    title: 'Multi-Database Support',
    description: 'Pro tier includes managed databases. PostgreSQL, MariaDB, Redis, and MongoDB—instant provisioning with connection strings.',
    gradient: 'from-purple-500 to-violet-500',
    details: [
      'PostgreSQL, MariaDB, Redis, MongoDB',
      'Instant database provisioning',
      'Connection pooling included',
      'Automatic backups and SSL',
    ],
  },
  {
    icon: 'language',
    title: 'Static Site Hosting',
    description: 'Deploy static sites with zero configuration. Upload your build output and get a live URL with SSL in seconds.',
    gradient: 'from-amber-500 to-orange-500',
    details: [
      'Zero-config deployment',
      'Free SSL certificates',
      'Custom domain support',
      'Version history and rollbacks',
    ],
  },
  {
    icon: 'analytics',
    title: 'Real-time Analytics',
    description: 'Monitor your site performance with detailed analytics. Page views, referrers, browsers, and more.',
    gradient: 'from-rose-500 to-pink-500',
    details: [
      'Page views and visitors',
      'Referrer tracking',
      'Browser and device insights',
      'Bandwidth monitoring',
    ],
  },
  {
    icon: 'groups',
    title: 'Organizations & Teams',
    description: 'Collaborate with your team using organizations. Manage members, roles, and shared resources from one dashboard.',
    gradient: 'from-teal-500 to-cyan-500',
    details: [
      'Multi-member organizations',
      'Role-based access control',
      'Shared projects and resources',
      'Team billing management',
    ],
  },
  {
    icon: 'security',
    title: 'Security First',
    description: 'Container isolation, SSL encryption, and API key authentication. Your deployments are secure by default.',
    gradient: 'from-red-500 to-rose-500',
    details: [
      'Container isolation',
      'SSL/TLS encryption',
      'API key scoping',
      'DDoS protection',
    ],
  },
];

export default function FeaturesPage() {
  return (
    <div className="pt-16">
      {/* Hero */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-orange-500/5 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Features Built for{' '}
            <span className="gradient-text">Modern Development</span>
          </h1>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            Everything you need to deploy, manage, and scale your web applications with AI-powered workflows.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className={`grid md:grid-cols-2 gap-8 items-center ${
                  index % 2 === 1 ? 'md:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6`}>
                    <span className="material-icons text-white text-3xl">{feature.icon}</span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                    {feature.title}
                  </h2>
                  <p className="text-lg text-zinc-400 mb-6">
                    {feature.description}
                  </p>
                  <ul className="space-y-3">
                    {feature.details.map((detail) => (
                      <li key={detail} className="flex items-center gap-3 text-zinc-300">
                        <span className="material-icons text-orange-400 text-lg">check_circle</span>
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8 ${index % 2 === 1 ? 'md:order-1' : ''}`}>
                  <div className="aspect-video bg-zinc-800/50 rounded-xl flex items-center justify-center">
                    <span className={`material-icons text-8xl bg-gradient-to-br ${feature.gradient} bg-clip-text text-transparent`}>
                      {feature.icon}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-raised">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to Experience These Features?
          </h2>
          <p className="text-lg text-zinc-400 mb-8">
            Start deploying with AI in minutes. Start free, upgrade to Pro when you're ready.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-orange-500 to-red-500 rounded-xl hover:shadow-glow transition-all"
            >
              <span className="material-icons">rocket_launch</span>
              Get Started
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 text-lg font-semibold text-zinc-300 bg-zinc-800 border border-zinc-700 rounded-xl hover:bg-zinc-700 transition-colors"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

